import dayjs from 'dayjs';
import { EPM_LIMITS, EPM_PATTERNS, hasNonWhitespace, testPattern } from './epmRules';
import { testId } from '../epmHelpers';

const SIGN_OFF_ID = 'sign_off';

/**
 * Step 1: Idea Completeness
 */
export const getIdeaCompleteness = (testData, { testLocations = [] } = {}) => {
    const items = [];
    const hypothesis = testData?.ideation?.hypothesis || '';
    const hypothesisTrimmed = typeof hypothesis === 'string' ? hypothesis.trim() : '';
    const isHypothesisValid = hasNonWhitespace(hypothesis) &&
        hypothesisTrimmed.length >= EPM_LIMITS.HYPOTHESIS_MIN &&
        hypothesisTrimmed.length <= EPM_LIMITS.HYPOTHESIS_MAX;

    items.push({
        id: 'hypothesis',
        label: `Hypothesis (${EPM_LIMITS.HYPOTHESIS_MIN}–${EPM_LIMITS.HYPOTHESIS_MAX} characters)`,
        isMet: isHypothesisValid,
        targetElementId: 'epm-field-hypothesis',
        helpText: hypothesisTrimmed.length === 0
            ? 'Required'
            : `${hypothesisTrimmed.length} characters (needs ${EPM_LIMITS.HYPOTHESIS_MIN}–${EPM_LIMITS.HYPOTHESIS_MAX})`
    });

    // Check all location fields configured as required
    const locationValues = testData?.ideation?.location || testData?.location || {};
    const requiredLocations = (testLocations || []).filter((loc) => loc.required);

    requiredLocations.forEach((loc) => {
        const val = locationValues[loc.key];
        let isMet = false;
        let note = 'Required';

        if (loc.field_type === 'dropdown') {
            isMet = Boolean(val && (loc.field_options || []).includes(val));
            if (!isMet && val) note = 'Must be one of the dropdown options';
        } else {
            const strVal = typeof val === 'string' ? val.trim() : '';
            const lengthValid = hasNonWhitespace(strVal) && strVal.length <= EPM_LIMITS.LOCATION_TEXT_MAX;
            const patternValid = !loc.validation_pattern || testPattern(loc.validation_pattern, strVal);
            isMet = lengthValid && patternValid;

            if (strVal.length > EPM_LIMITS.LOCATION_TEXT_MAX) {
                note = `Exceeds max ${EPM_LIMITS.LOCATION_TEXT_MAX} chars`;
            } else if (!patternValid) {
                note = 'Does not match required pattern';
            }
        }

        items.push({
            id: `location_${loc.key}`,
            label: `Location: ${loc.label}`,
            isMet,
            targetElementId: `epm-field-location-${loc.key}`,
            helpText: isMet ? undefined : note,
        });
    });

    // Tags requirement: at least 1 tag
    const tagCount = Array.isArray(testData?.tag_ids) ? testData.tag_ids.length : 0;
    items.push({
        id: 'tags',
        label: 'At least 1 tag selected',
        isMet: tagCount >= 1,
        targetElementId: 'epm-idea-tag-picker',
        helpText: tagCount === 0 ? 'Required for win rate insights' : undefined,
    });

    // Format integrity checks (mockup limit & evidence format)
    const mockupsCount = Array.isArray(testData?.ideation?.mockups) ? testData.ideation.mockups.length : 0;
    if (mockupsCount > EPM_LIMITS.MOCKUPS_MAX_COUNT) {
        items.push({
            id: 'mockups_limit',
            label: `Mockups limit (max ${EPM_LIMITS.MOCKUPS_MAX_COUNT} files)`,
            isMet: false,
            targetElementId: 'epm-field-mockups',
            helpText: `${mockupsCount} files uploaded`,
        });
    }

    const isComplete = items.every((i) => i.isMet);
    return { isComplete, items };
};

/**
 * Step 2: Priority Completeness
 */
export const getPriorityCompleteness = (testData) => {
    const items = [];
    const priority = testData?.priority || {};

    const isScoreValid = (val) => Number.isInteger(Number(val)) && Number(val) >= EPM_LIMITS.SCORE_MIN && Number(val) <= EPM_LIMITS.SCORE_MAX;

    items.push({
        id: 'impact',
        label: `Impact score (${EPM_LIMITS.SCORE_MIN}–${EPM_LIMITS.SCORE_MAX})`,
        isMet: isScoreValid(priority.impact),
        targetElementId: 'epm-field-impact',
    });

    items.push({
        id: 'confidence',
        label: `Confidence score (${EPM_LIMITS.SCORE_MIN}–${EPM_LIMITS.SCORE_MAX})`,
        isMet: isScoreValid(priority.confidence),
        targetElementId: 'epm-field-confidence',
    });

    items.push({
        id: 'ease',
        label: `Ease score (${EPM_LIMITS.SCORE_MIN}–${EPM_LIMITS.SCORE_MAX})`,
        isMet: isScoreValid(priority.ease),
        targetElementId: 'epm-field-ease',
    });

    const reason = testData?.triaged?.reason || '';
    const reasonTrimmed = typeof reason === 'string' ? reason.trim() : '';
    const isReasonValid = hasNonWhitespace(reason) &&
        reasonTrimmed.length >= EPM_LIMITS.DECISION_REASON_MIN &&
        reasonTrimmed.length <= EPM_LIMITS.DECISION_REASON_MAX;

    items.push({
        id: 'decision_reason',
        label: `Decision reason (${EPM_LIMITS.DECISION_REASON_MIN}–${EPM_LIMITS.DECISION_REASON_MAX} characters)`,
        isMet: isReasonValid,
        targetElementId: 'priority-reason',
        helpText: reasonTrimmed.length === 0
            ? 'Required'
            : reasonTrimmed.length < EPM_LIMITS.DECISION_REASON_MIN
            ? `Too short (${reasonTrimmed.length}/${EPM_LIMITS.DECISION_REASON_MIN})`
            : reasonTrimmed.length > EPM_LIMITS.DECISION_REASON_MAX
            ? `Too long (${reasonTrimmed.length}/${EPM_LIMITS.DECISION_REASON_MAX})`
            : undefined,
    });

    const isComplete = items.every((i) => i.isMet);
    return { isComplete, items };
};

/**
 * Step 3: Plan Completeness
 */
export const getPlanCompleteness = (testData) => {
    const items = [];
    const plans = testData?.plans || {};
    const primary = plans.primary_metric || '';
    const primaryTrimmed = typeof primary === 'string' ? primary.trim() : '';

    const isPrimaryValid = hasNonWhitespace(primary) &&
        primaryTrimmed.length >= EPM_LIMITS.PRIMARY_METRIC_MIN &&
        primaryTrimmed.length <= EPM_LIMITS.PRIMARY_METRIC_MAX;

    items.push({
        id: 'primary_metric',
        label: `Primary metric (${EPM_LIMITS.PRIMARY_METRIC_MIN}–${EPM_LIMITS.PRIMARY_METRIC_MAX} characters)`,
        isMet: isPrimaryValid,
        targetElementId: 'epm-field-primary-metric',
        helpText: primaryTrimmed.length === 0
            ? 'Required'
            : primaryTrimmed.length < EPM_LIMITS.PRIMARY_METRIC_MIN
            ? `Too short (${primaryTrimmed.length}/${EPM_LIMITS.PRIMARY_METRIC_MIN})`
            : primaryTrimmed.length > EPM_LIMITS.PRIMARY_METRIC_MAX
            ? `Too long (${primaryTrimmed.length}/${EPM_LIMITS.PRIMARY_METRIC_MAX})`
            : undefined,
    });

    // Secondary metrics limit check
    const secondaries = Array.isArray(plans.secondary_metric)
        ? plans.secondary_metric
        : typeof plans.secondary_metric === 'string' && plans.secondary_metric.trim()
        ? [plans.secondary_metric.trim()]
        : [];

    if (secondaries.length > EPM_LIMITS.SECONDARY_METRICS_MAX_COUNT) {
        items.push({
            id: 'secondary_metrics_count',
            label: `Max ${EPM_LIMITS.SECONDARY_METRICS_MAX_COUNT} secondary metrics allowed`,
            isMet: false,
            targetElementId: 'epm-field-secondary-metric',
            helpText: `${secondaries.length} entered`,
        });
    }

    const isComplete = items.every((i) => i.isMet);
    return { isComplete, items };
};

/**
 * Step 4: Build & QA Completeness
 */
export const getBuildQACompleteness = (
    testData,
    { stagingChecklist = [], productionChecklist = [], oneOffChecklists = [] } = {}
) => {
    const items = [];
    const variants = testData?.build_qa?.variants || [];

    const hasAtLeastOneVariant = Array.isArray(variants) && variants.length >= 1;
    const areVariantsValid = hasAtLeastOneVariant &&
        variants.length <= EPM_LIMITS.VARIANTS_MAX_COUNT &&
        variants.every((v) => {
            const details = typeof v.details === 'string' ? v.details.trim() : '';
            return details.length > 0 && details.length <= EPM_LIMITS.VARIANT_DETAILS_MAX && (v.source_url || v.key);
        });

    items.push({
        id: 'variants',
        label: `At least 1 variant (file + details up to ${EPM_LIMITS.VARIANT_DETAILS_MAX} characters)`,
        isMet: areVariantsValid,
        targetElementId: 'epm-field-variants',
        helpText: !hasAtLeastOneVariant
            ? 'At least 1 variant needed'
            : variants.length > EPM_LIMITS.VARIANTS_MAX_COUNT
            ? `Max ${EPM_LIMITS.VARIANTS_MAX_COUNT} variants allowed`
            : !areVariantsValid
            ? 'Each variant needs a file and details'
            : undefined,
    });

    const checkedIds = new Set((testData?.build_qa?.checklist || []).map(String));

    // Staging checklist REQ items
    const requiredStaging = [...stagingChecklist, ...(oneOffChecklists || []).filter((c) => (c.env || '').toUpperCase() === 'STAGING')]
        .filter((item) => (item.isRequired !== undefined ? item.isRequired : item.required !== false));

    const pendingStaging = requiredStaging.filter((item) => !checkedIds.has(String(item._id || item.id)));

    items.push({
        id: 'staging_checklist',
        label: 'Every required item in Staging checklist ticked',
        isMet: pendingStaging.length === 0,
        targetElementId: 'epm-staging-checklist',
        helpText: pendingStaging.length > 0 ? `${pendingStaging.length} open item(s)` : undefined,
    });

    // Production checklist REQ items (excluding sign_off item)
    const requiredProd = [...productionChecklist, ...(oneOffChecklists || []).filter((c) => (c.env || '').toUpperCase() === 'PRODUCTION')]
        .filter((item) => item.id !== SIGN_OFF_ID && item.key !== SIGN_OFF_ID)
        .filter((item) => (item.isRequired !== undefined ? item.isRequired : item.required !== false));

    const pendingProd = requiredProd.filter((item) => !checkedIds.has(String(item._id || item.id)));

    items.push({
        id: 'production_checklist',
        label: 'Every required item in Production checklist ticked',
        isMet: pendingProd.length === 0,
        targetElementId: 'epm-production-checklist',
        helpText: pendingProd.length > 0 ? `${pendingProd.length} open item(s)` : undefined,
    });

    const isComplete = items.every((i) => i.isMet);
    return { isComplete, items };
};

/**
 * Step 5: Schedule Completeness
 */
export const getScheduleCompleteness = (testData, { candidateTests = [], testLocations = [] } = {}) => {
    const items = [];
    const schedule = testData?.schedule || {};
    const startStr = schedule.date_time;
    const endStr = schedule.end_date_time;

    const start = dayjs(startStr);
    const end = dayjs(endStr);
    const hasValidStart = Boolean(startStr) && start.isValid();
    const hasValidEnd = Boolean(endStr) && end.isValid();
    const isStrictlyAfter = hasValidStart && hasValidEnd && start.isBefore(end);

    items.push({
        id: 'schedule_dates',
        label: 'Valid start & end date-time (start strictly before end)',
        isMet: hasValidStart && hasValidEnd && isStrictlyAfter,
        targetElementId: 'epm-schedule-dates',
        helpText: !hasValidStart || !hasValidEnd
            ? 'Start and end dates required'
            : !isStrictlyAfter
            ? 'End date must be strictly after start date'
            : undefined,
    });

    // Hard conflict check against candidate Live / Scheduled tests
    const currentId = String(testId(testData) || testData?.key || '');
    const currentLocation = testData?.ideation?.location || testData?.location || {};
    const activeHardLocations = (testLocations || []).filter(
        (loc) => (loc?.conflict_role || '').trim().toLowerCase() === 'hard'
    );

    let hardConflictCount = 0;

    if (hasValidStart && hasValidEnd && activeHardLocations.length > 0) {
        const curStart = start.startOf('day');
        const curEnd = end.startOf('day');

        candidateTests.forEach((other) => {
            if (other === testData) return;
            const otherId = String(testId(other) || other?.key || '');
            if (currentId && otherId && currentId === otherId) return;

            const otherStatus = (other?.status || '').toUpperCase();
            if (otherStatus !== 'LIVE' && otherStatus !== 'SCHEDULED') return;

            const oStartStr = other?.schedule?.date_time;
            const oEndStr = other?.schedule?.end_date_time;
            if (!oStartStr || !oEndStr) return;

            const oStart = dayjs(oStartStr).startOf('day');
            const oEnd = dayjs(oEndStr).startOf('day');
            if (!oStart.isValid() || !oEnd.isValid()) return;

            // Date overlap condition: max(curStart, oStart) <= min(curEnd, oEnd)
            const overlaps = curStart.isBefore(oEnd.add(1, 'second')) && oStart.isBefore(curEnd.add(1, 'second'));
            if (!overlaps) return;

            const otherLoc = other?.ideation?.location || other?.location || {};
            const matchesHard = activeHardLocations.some((field) => {
                const curVal = currentLocation[field.key];
                const othVal = otherLoc[field.key];
                return curVal != null && othVal != null &&
                    String(curVal).trim() !== '' &&
                    String(curVal).trim().toLowerCase() === String(othVal).trim().toLowerCase();
            });

            if (matchesHard) hardConflictCount += 1;
        });
    }

    items.push({
        id: 'schedule_conflicts',
        label: 'No hard conflicts with other Live or Scheduled tests',
        isMet: hardConflictCount === 0,
        targetElementId: 'epm-schedule-conflicts',
        helpText: hardConflictCount > 0 ? `${hardConflictCount} overlapping hard conflict(s)` : undefined,
    });

    const isComplete = items.every((i) => i.isMet);
    return { isComplete, items };
};

/**
 * Step 6: Results Completeness
 */
export const getResultsCompleteness = (testData, { learningText = '' } = {}) => {
    const items = [];
    const verdict = (testData?.verdict?.status || '').toLowerCase();
    const isVerdictValid = ['win', 'loss', 'inconclusive', 'invalid'].includes(verdict);

    items.push({
        id: 'verdict',
        label: 'Verdict chosen (Win, Loss, Inconclusive, or Invalid)',
        isMet: isVerdictValid,
        targetElementId: 'epm-field-verdict',
        helpText: isVerdictValid ? undefined : 'Choose a verdict',
    });

    const rationale = testData?.verdict?.reason || '';
    const rationaleTrimmed = typeof rationale === 'string' ? rationale.trim() : '';
    const isRationaleValid = hasNonWhitespace(rationale) &&
        rationaleTrimmed.length >= EPM_LIMITS.RATIONALE_MIN &&
        rationaleTrimmed.length <= EPM_LIMITS.RATIONALE_MAX;

    items.push({
        id: 'rationale',
        label: `Rationale written (${EPM_LIMITS.RATIONALE_MIN}–${EPM_LIMITS.RATIONALE_MAX} characters)`,
        isMet: isRationaleValid,
        targetElementId: 'epm-field-rationale',
        helpText: rationaleTrimmed.length === 0
            ? 'Required'
            : rationaleTrimmed.length < EPM_LIMITS.RATIONALE_MIN
            ? `Too short (${rationaleTrimmed.length}/${EPM_LIMITS.RATIONALE_MIN})`
            : rationaleTrimmed.length > EPM_LIMITS.RATIONALE_MAX
            ? `Too long (${rationaleTrimmed.length}/${EPM_LIMITS.RATIONALE_MAX})`
            : undefined,
    });

    const effectiveLearning =
        learningText ||
        testData?.learning?.current_assumption ||
        (typeof testData?.learning === 'string' ? testData?.learning : '') ||
        '';
    const learningTrimmed = typeof effectiveLearning === 'string' ? effectiveLearning.trim() : '';
    const isLearningValid = hasNonWhitespace(effectiveLearning) &&
        learningTrimmed.length >= EPM_LIMITS.WHAT_WE_NOW_BELIEVE_MIN &&
        learningTrimmed.length <= EPM_LIMITS.WHAT_WE_NOW_BELIEVE_MAX;

    items.push({
        id: 'learning',
        label: `'What we now believe' filled (${EPM_LIMITS.WHAT_WE_NOW_BELIEVE_MIN}–${EPM_LIMITS.WHAT_WE_NOW_BELIEVE_MAX} characters)`,
        isMet: isLearningValid,
        targetElementId: 'epm-field-learning',
        helpText: learningTrimmed.length === 0
            ? 'Required to record origin learning'
            : learningTrimmed.length < EPM_LIMITS.WHAT_WE_NOW_BELIEVE_MIN
            ? `Too short (${learningTrimmed.length}/${EPM_LIMITS.WHAT_WE_NOW_BELIEVE_MIN})`
            : learningTrimmed.length > EPM_LIMITS.WHAT_WE_NOW_BELIEVE_MAX
            ? `Too long (${learningTrimmed.length}/${EPM_LIMITS.WHAT_WE_NOW_BELIEVE_MAX})`
            : undefined,
    });

    // Result link format check (optional, but if provided must match)
    if (testData?.result_link && testData.result_link.trim()) {
        const link = testData.result_link.trim();
        const isLinkValid = link.length <= EPM_LIMITS.RESULT_LINK_MAX && EPM_PATTERNS.RESULT_LINK.test(link);
        if (!isLinkValid) {
            items.push({
                id: 'result_link_pattern',
                label: 'Result link must be a valid http(s) URL',
                isMet: false,
                targetElementId: 'epm-field-result-link',
                helpText: 'Must start with http:// or https://',
            });
        }
    }

    const isComplete = items.every((i) => i.isMet);
    return { isComplete, items };
};

/**
 * Unified getter for completeness status by step index (0 to 5)
 */
export const getStepCompleteness = (stepIndex, testData, context = {}) => {
    switch (stepIndex) {
        case 0:
            return getIdeaCompleteness(testData, { testLocations: context.testLocations || [] });
        case 1:
            return getPriorityCompleteness(testData);
        case 2:
            return getPlanCompleteness(testData);
        case 3:
            return getBuildQACompleteness(testData, {
                stagingChecklist: context.stagingChecklist || [],
                productionChecklist: context.productionChecklist || [],
                oneOffChecklists: context.oneOffChecklists || [],
            });
        case 4:
            return getScheduleCompleteness(testData, {
                candidateTests: context.candidateTests || context.tests || [],
                testLocations: context.testLocations || [],
            });
        case 5:
            return getResultsCompleteness(testData, {
                learningText: context.learningText || '',
            });
        default:
            return { isComplete: true, items: [] };
    }
};

